// Audio Analysis Service (leichte Variante ohne externe heavy Dependencies)

export interface AudioFeatures {
  bpm: number;
  key: string;
  energy: number;
  danceability: number;
  valence: number; // happiness/positivity
  loudness: number;
  tempo: number;
  spectralCentroid: number;
  duration: number;
}

class AudioAnalysisService {
  private audioContext: AudioContext | null = null;

  constructor() {
    this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  }

  /**
   * Analyze audio file and extract features
   */
  async analyzeAudio(file: File): Promise<AudioFeatures> {
    console.log('🎵 Starting audio analysis:', file.name);

    try {
      // Load and decode audio
      const audioBuffer = await this.loadAudioFile(file);
      
      // Extract features
      const bpm = await this.detectBPM(audioBuffer);
      const key = await this.detectKey(audioBuffer);
      const energy = this.calculateEnergy(audioBuffer);
      const duration = audioBuffer.duration;

      console.log('✅ Audio analysis complete:', { bpm, key, energy, duration });

      return {
        bpm,
        key,
        energy,
        danceability: this.estimateDanceability(bpm, energy),
        valence: 0.5, // TODO: Implement mood detection
        loudness: this.calculateLoudness(audioBuffer),
        tempo: bpm,
        spectralCentroid: 0, // TODO: Implement with Essentia
        duration,
      };
    } catch (error) {
      console.error('❌ Audio analysis failed:', error);
      throw error;
    }
  }

  /**
   * Load and decode audio file
   */
  private async loadAudioFile(file: File): Promise<AudioBuffer> {
    const arrayBuffer = await file.arrayBuffer();
    
    if (!this.audioContext) {
      throw new Error('AudioContext not initialized');
    }

    return await this.audioContext.decodeAudioData(arrayBuffer);
  }

  /**
   * Detect BPM using professional beat detection library with smart correction
   */
  private async detectBPM(audioBuffer: AudioBuffer): Promise<number> {
    // Minimal-Implementierung: nutze sofort Fallback, um Build ohne deps zu ermöglichen
    return this.detectBPMFallback(audioBuffer);
  }

  /**
   * Smart BPM correction - handles halftime/doubletime detection errors
   * Works universally for all genres
   */
  private correctBPM(rawBPM: number): number {
    // Generate candidates: quarter, half, original, double, quadruple
    const candidates = [
      rawBPM / 4,
      rawBPM / 2,
      rawBPM,
      rawBPM * 2,
      rawBPM * 4,
    ];
    
    // Score each candidate
    const scores = candidates.map(bpm => this.scoreBPM(bpm));
    
    // Find best candidate
    let bestBPM = rawBPM;
    let bestScore = -Infinity;
    
    for (let i = 0; i < candidates.length; i++) {
      if (scores[i] > bestScore) {
        bestScore = scores[i];
        bestBPM = candidates[i];
      }
    }
    
    console.log('🎯 BPM Candidates:', {
      'x0.25': `${Math.round(candidates[0])} (score: ${scores[0].toFixed(1)})`,
      'x0.5': `${Math.round(candidates[1])} (score: ${scores[1].toFixed(1)})`,
      'x1': `${Math.round(candidates[2])} (score: ${scores[2].toFixed(1)})`,
      'x2': `${Math.round(candidates[3])} (score: ${scores[3].toFixed(1)})`,
      'x4': `${Math.round(candidates[4])} (score: ${scores[4].toFixed(1)})`,
      '→ CHOSEN': Math.round(bestBPM)
    });
    
    return Math.round(bestBPM);
  }

  /**
   * Score a BPM candidate - UNIVERSAL for all genres
   */
  private scoreBPM(bpm: number): number {
    let score = 0;
    
    // Most music is between 60-180 BPM
    // Anything outside this is likely a detection error
    
    // Strong penalty for unrealistic BPM
    if (bpm < 60) {
      score -= 50; // Way too slow
    }
    if (bpm > 200) {
      score -= 50; // Way too fast (likely detected double time)
    }
    if (bpm > 180 && bpm <= 200) {
      score -= 20; // Probably too fast
    }
    
    // Realistic BPM range: 60-180
    if (bpm >= 60 && bpm <= 180) {
      score += 20; // Base score for realistic range
    }
    
    // Most popular range: 80-160 BPM (covers most genres)
    if (bpm >= 80 && bpm <= 160) {
      score += 15; // Extra points for most common range
    }
    
    // Sweet spot: 90-150 BPM (where 90% of music lives)
    if (bpm >= 90 && bpm <= 150) {
      score += 10;
    }
    
    // Slight preference for round numbers (90, 95, 100, 105, etc.)
    if (bpm % 5 === 0) {
      score += 2;
    }
    
    // Extra slight preference for numbers ending in 0 (100, 110, 120, etc.)
    if (bpm % 10 === 0) {
      score += 1;
    }
    
    return score;
  }

  /**
   * Fallback BPM detection with smart correction
   */
  private detectBPMFallback(audioBuffer: AudioBuffer): number {
    const channelData = audioBuffer.getChannelData(0);
    const sampleRate = audioBuffer.sampleRate;
    
    // Calculate energy in overlapping windows
    const windowSize = Math.floor(sampleRate * 0.05); // 50ms windows
    const hopSize = Math.floor(windowSize / 2);
    const energyValues: number[] = [];
    
    for (let i = 0; i < channelData.length - windowSize; i += hopSize) {
      const window = channelData.slice(i, i + windowSize);
      const energy = this.calculateFrameEnergy(window);
      energyValues.push(energy);
    }

    // Find peaks in energy (onsets)
    const peaks: number[] = [];
    const threshold = this.calculateThreshold(energyValues);
    
    for (let i = 1; i < energyValues.length - 1; i++) {
      if (energyValues[i] > threshold && 
          energyValues[i] > energyValues[i - 1] && 
          energyValues[i] > energyValues[i + 1]) {
        peaks.push(i * hopSize / sampleRate);
      }
    }

    if (peaks.length < 4) {
      return 140; // Default to common trap BPM
    }

    const intervals: number[] = [];
    for (let i = 1; i < peaks.length; i++) {
      intervals.push(peaks[i] - peaks[i - 1]);
    }

    intervals.sort((a, b) => a - b);
    const medianInterval = intervals[Math.floor(intervals.length / 2)];
    
    const rawBPM = 60 / medianInterval;
    
    // Use smart correction instead of simple while loops
    return this.correctBPM(rawBPM);
  }

  /**
   * Calculate adaptive threshold for peak detection
   */
  private calculateThreshold(values: number[]): number {
    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
    const stdDev = Math.sqrt(variance);
    return mean + stdDev * 0.5;
  }


  /**
   * Calculate energy of audio frame
   */
  private calculateFrameEnergy(frame: Float32Array): number {
    let sum = 0;
    for (let i = 0; i < frame.length; i++) {
      sum += frame[i] * frame[i];
    }
    return Math.sqrt(sum / frame.length);
  }

  /**
   * Detect musical key - SIMPLIFIED VERSION
   * Returns common trap/hip-hop keys or empty string if uncertain
   */
  private async detectKey(_audioBuffer: AudioBuffer): Promise<string> {
    // Key detection is very complex and often inaccurate
    // For beats/instrumentals, it's better to let the user set it manually
    // or use OpenAI for accurate detection
    
    // Return empty string - user should set manually or use OpenAI
    console.log('💡 Key detection: Please set manually or use OpenAI for accuracy');
    return '';
  }

  /**
   * Calculate overall energy
   */
  private calculateEnergy(audioBuffer: AudioBuffer): number {
    const channelData = audioBuffer.getChannelData(0);
    let sum = 0;

    for (let i = 0; i < channelData.length; i++) {
      sum += Math.abs(channelData[i]);
    }

    const avgAmplitude = sum / channelData.length;
    return Math.min(1, avgAmplitude * 10); // Normalize to 0-1
  }

  /**
   * Calculate loudness (RMS)
   */
  private calculateLoudness(audioBuffer: AudioBuffer): number {
    const channelData = audioBuffer.getChannelData(0);
    let sum = 0;

    for (let i = 0; i < channelData.length; i++) {
      sum += channelData[i] * channelData[i];
    }

    const rms = Math.sqrt(sum / channelData.length);
    return -10 * Math.log10(rms + 0.0001); // Convert to dB
  }

  /**
   * Estimate danceability from BPM and energy
   */
  private estimateDanceability(bpm: number, energy: number): number {
    // Optimal BPM for dancing is around 120-140
    const bpmFactor = 1 - Math.abs(130 - bpm) / 130;
    const danceability = (bpmFactor * 0.6 + energy * 0.4);
    
    return Math.max(0, Math.min(1, danceability));
  }
}

export const audioAnalysisService = new AudioAnalysisService();


